import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Flag, Calendar, Pill, Heart, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fhirDataStore } from '@/utils/fhirDataUtils';

interface RecordItem {
  id: string;
  type: 'condition' | 'medication' | 'lab' | 'procedure';
  title: string;
  description: string;
  date: string;
  provider: string;
  flagged: boolean;
  reason?: string;
  severity: 'high' | 'medium' | 'low';
  fhirResource?: any;
}

interface RecordReviewProps {
  selectedPatientId?: string;
  onItemsFlagged?: (items: RecordItem[]) => void;
}

export const RecordReview = ({ selectedPatientId = 'me', onItemsFlagged }: RecordReviewProps) => {
  const { toast } = useToast();
  const [recordItems, setRecordItems] = useState<RecordItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecordItems();
  }, [selectedPatientId]);

  const loadRecordItems = async () => {
    setLoading(true);
    try {
      const items: RecordItem[] = [];
      
      if (selectedPatientId === 'me') {
        // Load from localStorage for 'me'
        const storedData = localStorage.getItem('fhir_data');
        if (storedData) {
          const bundle = JSON.parse(storedData);
          items.push(...extractRecordsFromBundle(bundle));
        }
      } else {
        // Load from IndexedDB for other patients - using the correct method name
        const patientData = await fhirDataStore.getFHIRData(selectedPatientId);
        if (patientData) {
          items.push(...extractRecordsFromBundle(patientData));
        }
      }
      
      setRecordItems(items);
    } catch (error) {
      console.error('Error loading patient records:', error);
      // Fallback to mock data if real data fails
      setRecordItems(getMockRecordItems());
    }
    setLoading(false);
  };

  const extractRecordsFromBundle = (bundle: any): RecordItem[] => {
    const items: RecordItem[] = [];
    
    if (!bundle?.entry) return items;

    bundle.entry.forEach((entry: any) => {
      const resource = entry.resource;
      if (!resource) return;

      switch (resource.resourceType) {
        case 'Condition':
          items.push({
            id: resource.id || `condition-${Math.random()}`,
            type: 'condition',
            title: resource.code?.coding?.[0]?.display || resource.code?.text || 'Unknown Condition',
            description: `Status: ${resource.clinicalStatus?.coding?.[0]?.code || 'unknown'}`,
            date: resource.onsetDateTime || resource.recordedDate || '2024-01-01',
            provider: resource.recorder?.display || 'Unknown Provider',
            flagged: true,
            reason: 'Review clinical status and current relevance',
            severity: resource.severity?.coding?.[0]?.code === 'severe' ? 'high' : 'medium',
            fhirResource: resource
          });
          break;
          
        case 'MedicationStatement':
        case 'MedicationRequest':
          items.push({
            id: resource.id || `medication-${Math.random()}`,
            type: 'medication',
            title: resource.medicationCodeableConcept?.coding?.[0]?.display || 
                   resource.medicationCodeableConcept?.text || 'Unknown Medication',
            description: `Status: ${resource.status || 'unknown'}`,
            date: resource.effectiveDateTime || resource.authoredOn || '2024-01-01',
            provider: resource.requester?.display || 'Unknown Provider',
            flagged: true,
            reason: 'Verify current dosage and active status',
            severity: 'medium',
            fhirResource: resource
          });
          break;
      }
    });

    return items;
  };

  const getMockRecordItems = (): RecordItem[] => [
    {
      id: '1',
      type: 'condition',
      title: 'Hypertension',
      description: 'Essential hypertension - still listed as active',
      date: '2020-03-15',
      provider: 'Dr. Sarah Smith',
      flagged: true,
      reason: 'This condition may be outdated - last BP readings were normal',
      severity: 'high'
    },
    {
      id: '2',
      type: 'medication',
      title: 'Lisinopril 10mg',
      description: 'ACE inhibitor prescribed for hypertension',
      date: '2024-01-10',
      provider: 'Dr. Sarah Smith',
      flagged: true,
      reason: 'Dosage may be incorrect - current prescription shows 5mg',
      severity: 'medium'
    }
  ];

  const getIcon = (type: string) => {
    switch (type) {
      case 'condition': return <Heart className="h-4 w-4" />;
      case 'medication': return <Pill className="h-4 w-4" />;
      case 'lab': return <FileText className="h-4 w-4" />;
      case 'procedure': return <FileText className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleItemSelection = (itemId: string, checked: boolean) => {
    setSelectedItems(prev => 
      checked 
        ? [...prev, itemId]
        : prev.filter(id => id !== itemId)
    );
  };

  const handleFlagForDispute = () => {
    if (selectedItems.length === 0) {
      toast({
        title: "No items selected",
        description: "Please select at least one item to flag for dispute.",
        variant: "destructive",
      });
      return;
    }

    const flaggedItems = recordItems.filter(item => selectedItems.includes(item.id));
    onItemsFlagged?.(flaggedItems);

    toast({
      title: "Items flagged for dispute",
      description: `${selectedItems.length} items have been flagged and will be included in your dispute packet.`,
    });

    setSelectedItems([]);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading patient records...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center space-x-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
          <h3 className="text-lg font-semibold">Record Review Checklist</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Review your health records for potential errors that could affect your Health Score. 
          Check items that appear incorrect or outdated.
        </p>
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            {selectedItems.length} items selected for dispute
          </p>
          <Button 
            onClick={handleFlagForDispute}
            disabled={selectedItems.length === 0}
          >
            <Flag className="h-4 w-4 mr-2" />
            Flag Selected Items ({selectedItems.length})
          </Button>
        </div>
      </Card>

      <div className="space-y-3">
        {recordItems.length === 0 ? (
          <Card className="p-6">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No health records found for review</p>
              <p className="text-sm mt-2">Upload your health data to see records here</p>
            </div>
          </Card>
        ) : (
          recordItems.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={(checked) => handleItemSelection(item.id, !!checked)}
                  className="mt-1"
                />
                
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getIcon(item.type)}
                      <h4 className="font-medium">{item.title}</h4>
                      <Badge className={getSeverityColor(item.severity)}>
                        {item.severity} priority
                      </Badge>
                    </div>
                    {item.flagged && (
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                  
                  {item.reason && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-3">
                      <p className="text-sm text-orange-800">
                        <strong>Potential Issue:</strong> {item.reason}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(item.date).toLocaleDateString()}</span>
                    </div>
                    <span>Provider: {item.provider}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
